import { useState } from "react";
import type { FxDeal } from "../../lib/fx.ts";
import { todayIso } from "../../lib/format.ts";
import { sicherungsNr, sicherungsZusatz } from "../../lib/sicherungsNummer.ts";
import { hatRecht } from "../../state/berechtigungen.ts";
import { Ikone } from "../Ikone.tsx";
import { datum, kurs, usd } from "./fxAnzeige.ts";

export type SicherungsStatus = "offen" | "uebernommen" | "geloescht";

export interface SicherungenTabelleProps {
  /** Alle nicht gelöschten Sicherungen (offen und übernommen). */
  deals: readonly FxDeal[];
  /** Weich gelöschte, nie übernommene Sicherungen. */
  geloeschteDeals: readonly FxDeal[];
  rechte: ReadonlySet<string>;
  kannAnlegen: boolean;
  kannLoeschen: boolean;
  onNeu: () => void;
  onUebernehmen: (deal: FxDeal) => void;
  onLoeschen: (deal: FxDeal, ausloeser: HTMLElement) => void;
  /** Die Bank einer Sicherung: Name des wirksamen Kontos (Cutover-Zuordnung), sonst der Banktext am Deal. */
  bankVon?: (deal: FxDeal) => string;
  /** Oeffnet das Detail-Panel "Sicherung S-n" (Zeilenklick); ohne Angabe sind die Zeilen nicht klickbar. */
  onOeffnen?: (deal: FxDeal, ausloeser: HTMLElement) => void;
  /** Heutiger Kalendertag (YYYY-MM-DD), für "fällig"; Vorgabe: heute. */
  heute?: string;
  neuAusloeserRef?: React.Ref<HTMLButtonElement>;
}

const istOffen = (deal: FxDeal) => deal.snapshotId === null || deal.snapshotId === undefined;
const nachStichtag = (a: FxDeal, b: FxDeal) => String(a.date ?? "").localeCompare(String(b.date ?? "")) || sicherungsNr(a).localeCompare(sicherungsNr(b), "de", { numeric: true });

/** Zeitpunkt der Löschung als deutsches Datum (Tooltip). */
const loeschDatum = (zeitpunkt: unknown) => new Date(String(zeitpunkt)).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

/**
 * Karte "Sicherungen": die einzige Liste der Kurssicherungen auf der Zahlungsseite; ein Klick auf die Zeile oeffnet das
 * Detail-Panel (Beleg, Bindung, Bearbeiten). Statusfilter als Pills,
 * Sortierung nach Stichtag, überfällige offene Zeilen markiert, Summe der gefilterten Beträge in der Fußzeile.
 */
export function SicherungenTabelle({ deals, geloeschteDeals, rechte, kannAnlegen, kannLoeschen, onNeu, onUebernehmen, onLoeschen, bankVon = (deal) => String(deal.bank ?? ""), onOeffnen, heute = todayIso(), neuAusloeserRef }: SicherungenTabelleProps) {
  const [status, setStatus] = useState<SicherungsStatus>("offen");
  const offene = deals.filter(istOffen).slice().sort(nachStichtag);
  const uebernommene = deals.filter((deal) => !istOffen(deal)).slice().sort(nachStichtag);
  const geloeschte = geloeschteDeals.slice().sort(nachStichtag);
  const zeilen = status === "offen" ? offene : status === "uebernommen" ? uebernommene : geloeschte;
  const summe = zeilen.reduce((s, deal) => s + (Number(deal.usd) || 0), 0);
  const darfUebernehmen = hatRecht(rechte, "manage_fx");
  const pills: [SicherungsStatus, string, number][] = [
    ["offen", "Offen", offene.length],
    ["uebernommen", "Übernommen", uebernommene.length],
    ["geloescht", "Gelöscht", geloeschte.length],
  ];

  return (
    <section className="fx-overview-card fx-sicherungen" aria-labelledby="fx-sicherungen-titel">
      <div className="fx-overview-card-head">
        <h2 id="fx-sicherungen-titel">Sicherungen</h2>
        <button ref={neuAusloeserRef} type="button" className="btn-primary" disabled={!kannAnlegen} title={!hatRecht(rechte, "manage_fx") ? "fehlende Berechtigung" : undefined} onClick={onNeu}>+ Neue Sicherung</button>
      </div>
      <div className="fx-pills" role="group" aria-label="Status">
        {pills.map(([id, label, anzahl]) => <button type="button" key={id} className={`fx-pill${status === id ? " active" : ""}`} aria-pressed={status === id} onClick={() => setStatus(id)}>{label} ({anzahl})</button>)}
      </div>
      <div className="fx-tabelle-rahmen">
        <table className="fx-tabelle fx-sicherungen-tabelle">
          <thead><tr><th scope="col">Nr.</th><th scope="col">Stichtag</th><th scope="col" className="fx-zahl">Betrag USD</th><th scope="col" className="fx-zahl">Kurs</th><th scope="col">Bank</th><th scope="col" className="fx-aktionen">Aktionen</th></tr></thead>
          <tbody>
            {zeilen.map((deal) => {
              const faellig = status === "offen" && Boolean(deal.date) && String(deal.date) < heute;
              const zusatz = sicherungsZusatz(deal);
              const oeffnet = Boolean(onOeffnen) && status !== "geloescht";
              return (
                <tr key={String(deal.id)} className={`${faellig ? "fx-zeile-faellig" : ""}${status === "geloescht" ? " fx-zeile-geloescht" : ""}${oeffnet ? " fx-zeile-klickbar" : ""}`.trim() || undefined} data-testid={status === "geloescht" ? "fx-deal-geloescht" : undefined} onClick={oeffnet ? (ereignis) => onOeffnen?.(deal, ereignis.currentTarget) : undefined}>
                  <td className="fx-nr">{oeffnet ? <button type="button" className="link-btn fx-nr-knopf" aria-label={`Sicherung ${sicherungsNr(deal)} öffnen`} title={zusatz ? `Bisherige Nummer: ${zusatz}` : "Details öffnen"} onClick={(ereignis) => { ereignis.stopPropagation(); onOeffnen?.(deal, ereignis.currentTarget); }}>{sicherungsNr(deal)}</button> : <span title={zusatz ? `Bisherige Nummer: ${zusatz}` : undefined}>{sicherungsNr(deal)}</span>}</td>
                  <td>{datum(deal.date)}{faellig && <span className="fx-badge fx-badge-warn">fällig</span>}</td>
                  <td className="fx-zahl">{usd(deal.usd)}</td>
                  <td className="fx-zahl">{kurs(deal.rate)}</td>
                  <td>{bankVon(deal) || "—"}</td>
                  <td className="fx-aktionen">
                    {status === "offen" && <>
                      <button type="button" className="btn-sec fx-klein" disabled={!darfUebernehmen} title={!darfUebernehmen ? "fehlende Berechtigung" : undefined} aria-label={`Sicherung ${sicherungsNr(deal)} übernehmen`} onClick={(ereignis) => { ereignis.stopPropagation(); onUebernehmen(deal); }}>Übernehmen</button>
                      {kannLoeschen && <button type="button" className="fx-icon-knopf" aria-label={`Sicherung ${sicherungsNr(deal)} löschen`} title="Löschen" onClick={(ereignis) => { ereignis.stopPropagation(); onLoeschen(deal, ereignis.currentTarget); }}><Ikone name="trash-2" /></button>}
                    </>}
                    {status === "uebernommen" && <span className="fx-zart">übernommen</span>}
                    {status === "geloescht" && <span className="fx-zart" title={[deal.deletedAt ? `gelöscht am ${loeschDatum(deal.deletedAt)}` : "", deal.deletedByName ? `von ${String(deal.deletedByName)}` : "", deal.deleteReason ? `Grund: ${String(deal.deleteReason)}` : ""].filter(Boolean).join(" · ") || undefined}>gelöscht · nicht übernommen</span>}
                  </td>
                </tr>
              );
            })}
            {!zeilen.length && <tr><td colSpan={6} className="fx-leer">{status === "offen" ? "Keine offenen Sicherungen." : status === "uebernommen" ? "Keine übernommenen Sicherungen." : "Keine gelöschten Sicherungen."}</td></tr>}
          </tbody>
          <tfoot><tr><th scope="row" colSpan={2}>Summe ({zeilen.length})</th><td className="fx-zahl">{usd(summe)}</td><td colSpan={3} /></tr></tfoot>
        </table>
      </div>
    </section>
  );
}
